import {Box} from 'src/design/atoms/Box';
import React from 'react';
import type {
  TaskAssignmentSimpleResponse,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {useGetTasksSupervisorApprovalListQuery} from 'src/redux/queries/taskDefinition';
import {Description} from 'src/design/atoms/Description';
import {
  TasksDataListMeta,
  taskAssignmentRowSubText,
} from 'src/app/tasks/taskHelpers';
import {DataHeader} from 'src/design/organisms/DataHeader';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useDebounce from 'src/shared/hooks/useDebounce';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const maxPageRows = 10;

export interface SupervisorApprovalListProps {
  taskType: TaskTypeEnum;
}

export const SupervisorApprovalList: React.FC<SupervisorApprovalListProps> = ({
  taskType,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const {
    searchTerm,
    setSearchTerm,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState({
    identifier: `supervisor-approvals-${taskType}`,
    pageSize: maxPageRows,
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const {data, isLoading, error} = useGetTasksSupervisorApprovalListQuery({
    taskType: taskType,
    start,
    length,
    search: debouncedSearchTerm ? debouncedSearchTerm : undefined,
  });

  React.useEffect(() => {
    if (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [error, setAlert]);

  const indexListData = data?.data as TaskAssignmentSimpleResponse[] | undefined;
  const totalRecords = data?.totalRecords ?? 0;

  const handleNavigateToTask = (id: string | number, item: any) =>
    navigate(PathUtility.ViewTaskSupervisorApproval(taskType, item.id));

  const handleSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {value} = e.target;
    setSearchTerm(value);
  };

  return (
    <>
      <Box mb="md">
        <Description text="Manage supervisor approvals below. If there are any open tasks that you may approve, they will be listed below. Click on the task to begin the approval process." />
      </Box>
      <DataHeader
        titleText={''}
        searchTerm={searchTerm}
        actions={[]}
        filters={[]}
        onSearchTermChange={handleSearchTerm}
        isFilterActive={false}
      />

      <DataList
        isLoading={isLoading}
        data={indexListData}
        meta={TasksDataListMeta}
        currentPage={currentPage}
        totalRecords={totalRecords}
        recordsPerPage={maxPageRows}
        onRowClick={handleNavigateToTask}
        onPageChange={handlePageChange}
        onRowSubTextRender={taskAssignmentRowSubText}
        hasSearchTerm={searchTerm.trim().length > 0}
        hasCreationLogic={true}
        noDataText="There are currently no open items."
      />
    </>
  );
};
